import React, { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { motion } from 'framer-motion'
import { format } from 'date-fns'
import { X, Clock, PaperPlaneRight, CircleNotch, WarningCircle } from '@phosphor-icons/react'
import { workflowsApi, getErrorMessage, type PendingApproval } from '@/lib/api'
import ReadOnlyWorkflowCanvas, {
  type RawWorkflowEdge,
  type RawWorkflowNode,
} from '@/components/workflow/ReadOnlyWorkflowCanvas'
import DecisionFooter from '@/components/approvals/DecisionFooter'
import { nodeSummary } from '@/lib/workflowNodeMeta'
import { useScrollLock } from '@/lib/useScrollLock'

/**
 * Settings > Workflow Approvals: the in-place review for one activation
 * request (brief C2). Fetches the workflow on open so the approver sees the
 * exact graph the Rep saved, drawn read-only, plus a per-type node count.
 * Decisions go through the props (the section wires them to its store);
 * the modal only closes itself once the call resolves, so a failed call
 * (already toasted by the store) leaves the approver on the review.
 */
interface Props {
  request: PendingApproval | null
  onClose: () => void
  onApprove: () => Promise<void>
  onReject: (reason: string) => Promise<void>
}

interface LoadedWorkflow {
  name: string
  nodes: RawWorkflowNode[]
  edges: RawWorkflowEdge[]
}

export function formatRequestedAt(iso: string | null | undefined): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return format(d, 'd MMM yyyy, HH:mm')
}

const chipClass =
  'inline-flex items-center gap-1 rounded-full border border-border/60 dark:border-white/[0.1] ' +
  'bg-muted/50 dark:bg-white/[0.04] px-2.5 py-0.5 text-foreground dark:text-[#e2e4e7]'

const WorkflowReviewModal: React.FC<Props> = ({ request, onClose, onApprove, onReject }) => {
  const open = request != null
  const workflowId = request?.workflow_id ?? null
  const [workflow, setWorkflow] = useState<LoadedWorkflow | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useScrollLock(open)

  useEffect(() => {
    if (workflowId == null) return
    let cancelled = false
    setWorkflow(null)
    setError(null)
    setLoading(true)
    workflowsApi
      .getById(workflowId)
      .then(res => {
        if (cancelled) return
        const data = res.data || {}
        setWorkflow({
          name: data.name ?? '',
          nodes: Array.isArray(data.nodes) ? data.nodes : [],
          edges: Array.isArray(data.edges) ? data.edges : [],
        })
      })
      .catch(err => {
        if (cancelled) return
        setError(getErrorMessage(err, 'Failed to load this workflow'))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [workflowId])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, busy, onClose])

  if (!request) return null

  const run = async (fn: () => Promise<void>) => {
    setBusy(true)
    try {
      await fn()
      onClose()
    } catch {
      // The store already toasted; stay open so the approver can retry.
    } finally {
      setBusy(false)
    }
  }

  const title = workflow?.name || request.workflow_name
  const requestedAt = formatRequestedAt(request.requested_at)
  const summary = workflow ? nodeSummary(workflow.nodes) : []

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md"
      role="dialog"
      aria-modal="true"
      aria-label={`Review workflow ${title}`}
    >
      <motion.div
        className="fixed top-0 left-0 w-screen h-screen"
        onClick={busy ? undefined : onClose}
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.15 }}
      />
      <motion.div
        className="relative z-10 flex max-h-[90vh] w-full max-w-4xl flex-col rounded-2xl border border-border/60 bg-card shadow-2xl dark:border-white/[0.08] dark:bg-[#0f1011]"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        initial={{ opacity: 0, y: 8, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="flex items-start justify-between gap-3 px-6 pt-6 pb-4">
          <div className="min-w-0">
            <p
              className="text-muted-foreground dark:text-[#8a8f98] uppercase"
              style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '0.06em' }}
            >
              Workflow activation request
            </p>
            <h2
              className="text-foreground dark:text-[#f7f8f8] truncate"
              style={{ fontSize: '18px', fontWeight: 590, lineHeight: 1.33, letterSpacing: '-0.24px' }}
            >
              {title}
            </h2>
            <div
              className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
            >
              <span className="inline-flex items-center gap-1.5">
                <Clock className="h-3 w-3" />
                Requested by {request.requested_by_name}
                {requestedAt ? ` on ${requestedAt}` : ''}
              </span>
              {request.approver_name && (
                <span className="inline-flex items-center gap-1.5">
                  <PaperPlaneRight className="h-3 w-3" />
                  Sent to {request.approver_name}
                </span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label="Close review"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.04] transition-colors duration-150"
          >
            <X className="h-4 w-4" weight="regular" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 space-y-3">
          {loading && (
            <div
              className="flex items-center gap-2 py-10 justify-center text-muted-foreground dark:text-[#8a8f98]"
              style={{ fontSize: '13px' }}
              role="status"
            >
              <CircleNotch className="h-4 w-4 animate-spin" />
              Loading workflow
            </div>
          )}
          {error && !loading && (
            <p
              className="flex items-center gap-2 text-red-700 dark:text-red-300"
              style={{ fontSize: '13px', letterSpacing: '-0.13px' }}
              role="alert"
            >
              <WarningCircle className="h-4 w-4" weight="fill" />
              {error}
            </p>
          )}
          {workflow && !loading && (
            <>
              <ReadOnlyWorkflowCanvas nodes={workflow.nodes} edges={workflow.edges} height={360} />
              <div className="flex flex-wrap items-center gap-1.5" data-testid="node-summary">
                {summary.length === 0 ? (
                  <span
                    className="text-muted-foreground dark:text-[#8a8f98]"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    This workflow has no steps yet.
                  </span>
                ) : (
                  summary.map(row => (
                    <span key={row.type} className={chipClass} style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>
                      {row.count} {row.kind}
                    </span>
                  ))
                )}
              </div>
            </>
          )}
        </div>

        <div className="border-t border-border/50 dark:border-white/[0.06] px-6 py-4">
          <DecisionFooter
            approveLabel="Approve"
            onApprove={() => run(onApprove)}
            onReject={reason => run(() => onReject(reason))}
            busy={busy}
          />
        </div>
      </motion.div>
    </div>,
    document.body,
  )
}

export default WorkflowReviewModal
